import React, { useState } from 'react';
import { AlertTriangle, FileText, Trash2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';

const DraftLimitModal = ({ isOpen, onClose, onDelete, drafts, maxDrafts }) => {
  const [deleting, setDeleting] = useState(null);

  if (!isOpen) return null;

  const handleDelete = async (willId) => {
    setDeleting(willId);
    await onDelete(willId);
    setDeleting(null);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-2xl max-w-lg w-full shadow-2xl border border-slate-700 overflow-hidden">
        {/* Header */}
        <div className="bg-amber-500/10 px-6 py-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Draft Limit Reached</h2>
              <p className="text-slate-400 text-sm">
                You have {drafts.length} of {maxDrafts} unpaid drafts
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-slate-300 mb-4">
            You've reached the maximum number of unpaid draft wills. Please delete an existing draft or complete payment on one to create a new will.
          </p>

          {/* Draft List */}
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {drafts.map((draft) => (
              <div 
                key={draft.id}
                className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-700/50 rounded-lg">
                    <FileText className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">
                      {draft.testator_name || 'Unnamed Will'}
                    </p>
                    <p className="text-slate-400 text-xs">
                      Step {draft.progress?.current_step || 1} of 9 • 
                      Created {new Date(draft.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(draft.id)}
                  disabled={deleting === draft.id}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid={`delete-draft-${draft.id}`}
                >
                  {deleting === draft.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-800/50 px-6 py-4 border-t border-slate-700">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
            data-testid="draft-limit-close"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DraftLimitModal;
